import { useMediaQuery } from "@react-hookz/web";
import type { Row } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useLayoutEffect } from "react";

import type { StockProduct } from "~/views/stock/stock-product-list";
import { StockProductListItem } from "~/views/stock/stock-product-list-item";

export function StockProductVirtualList({
  pageRef,
  rows,
}: {
  pageRef: React.RefObject<HTMLDivElement | null>;
  rows: Row<StockProduct>[];
}) {
  const isWide = useMediaQuery("(min-width: 961px)");
  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => (isWide ? 88 : 197),
    getScrollElement: () => pageRef.current,
    overscan: 5,
  });

  // Re-measure items when the layout changes
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [virtualizer, isWide]);

  const virtualItems = virtualizer.getVirtualItems();

  const [paddingTop, paddingBottom] =
    virtualItems.length > 0
      ? [
          Math.max(
            0,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            virtualItems[0]!.start - virtualizer.options.scrollMargin,
          ),
          Math.max(
            0,
            virtualizer.getTotalSize() -
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              virtualItems[virtualItems.length - 1]!.end,
          ),
        ]
      : [0, 0];

  return (
    <div
      style={{
        height: `${virtualizer.getTotalSize()}px`,
      }}
    >
      {paddingTop > 0 ? <div style={{ height: paddingTop }}></div> : null}
      {virtualItems.map((virtualItem) => {
        const row = rows[virtualItem.index];
        if (!row) return null;
        return (
          <StockProductListItem
            data-index={virtualItem.index}
            key={virtualItem.key}
            ref={virtualizer.measureElement}
            row={row}
          />
        );
      })}
      {paddingBottom > 0 ? <div style={{ height: paddingBottom }}></div> : null}
    </div>
  );
}
